import React, { useEffect, useState, useRef } from 'react';
import { Parser } from 'html-to-react';
import _ from 'lodash';
import { interpolateAdSourceWithTrackingId } from '../ad/utils';
import Handlebars, { compileHandlebarsTemplate } from '../utils/Handlebars';
import ProductsApi from '../api/ProductsApi';
import AdsApi from '../api/AdsApi';
import { injectHtml, isElementChildOf } from '../utils/DomUtils';
import ClientAnalytics from '../analytics/ClientAnalytics';
import { usePrevious } from '../common/hooks';
import { isElementOrItsChildrenVisible } from '../utils/BrowserUtils';
import { AdVisibilityObserver } from '../ad/AdVisibilityObserver';

const CommerceUnit = (props) => {
    const { adRefreshInterval, productRefreshInterval, programmaticAdTimeToShow, applyTrackingCodes, publisherCssReset = '' } = props;

    function compileAdRendererTemplate() {
        const template = getTemplateContent();
        return compileHandlebarsTemplate(Handlebars, template);
    }

    function getTemplateContent() {
        return _.get(props.adRenderer, 'template.templateContent');
    }

    const productRefreshTimer = useRef(null);
    const cachedProductDataSources = useRef({});
    const injectedAds = useRef({});
    // compile template before initial render to enable server-side rendering
    const compiledAdTemplate = useRef(compileAdRendererTemplate());

    // tracking
    const root = useRef(null);
    const isLoadTracked = useRef(false);
    const commerceUnitViewObserver = useRef(null);
    const adRefreshTimer = useRef(null);
    const iframeAdClickDetectionInterval = useRef(null);

    const [products, setProducts] = useState([]);
    const [adSources, setAdSources] = useState([]);
    const [adRenderer, setAdRenderer] = useState(null);

    const prevProducts = usePrevious(products);
    const prevAdSources = usePrevious(adSources);
    const isNewProductAdLoad = (productId) => {
        return !_.find(prevProducts, { id: productId });
    };
    const isNewProgrammaticAdLoad = (adSourceId) => {
        return !_.find(prevAdSources, { id: adSourceId });
    };
    const getRemovedProductAds = () => {
        return _.differenceBy(prevProducts, products, 'id');
    };
    const getRemovedProgrammaticAds = () => {
        return _.differenceBy(prevAdSources, adSources, 'id');
    };

    const productAdViewObservers = useRef({});
    const programmaticAdViewObservers = useRef({});

    const productAdViewTracked = useRef({});
    const programmaticAdViewTracked = useRef({});

    useEffect(() => {
        // equivalent of component mount
        initialize();

        return () => {
            // clean up on unmount
            if (productRefreshTimer.current) clearInterval(productRefreshTimer.current);
            if (adRefreshTimer.current) clearInterval(adRefreshTimer.current);
            if (iframeAdClickDetectionInterval.current) clearInterval(iframeAdClickDetectionInterval.current);
            if (commerceUnitViewObserver.current) {
                commerceUnitViewObserver.current.disconnect();
            }
        };
    }, []);

    useEffect(() => {
        if (!adRenderer) return;

        loadDisplayedAdsData();
        loadDisplayedProductData();

        if (productRefreshInterval) {
            productRefreshTimer.current = setInterval(() => loadDisplayedProductData(), productRefreshInterval * 1000);
        }
        if (adRefreshInterval) {
            adRefreshTimer.current = setInterval(() => loadDisplayedAdsData(), adRefreshInterval * 1000);
        }

        injectTemplateScript();

        if (!isLoadTracked.current) {
            trackCommerceUnitEvent('load');
            applyTrackingCodes && applyTrackingCodes('load');
            isLoadTracked.current = true;
        }

        commerceUnitViewObserver.current = new AdVisibilityObserver(root.current, () => {
            trackCommerceUnitEvent('view');
        });
    }, [adRenderer]);

    useEffect(() => {
        if (_.isEmpty(products)) return;

        setupProductAdTracking();
    }, [products]);

    useEffect(() => {
        if (_.isEmpty(adSources)) return;

        injectProgrammaticAdSources();
        setupProgrammaticAdClickTracking();
    }, [adSources]);

    /**
     * Inject programmatic ad code in a way that executes the injected script elements
     */
    const injectProgrammaticAdSources = () => {
        const programmaticAdRoots = getProgrammaticAdRoots();
        for (const [adRoot, id] of programmaticAdRoots) {
            const adCodeContainer = adRoot.querySelectorAll('[data-vp-programmatic-ad-code]')[0];
            if (!adCodeContainer) return;
            if (injectedAds.current[adCodeContainer] === id) return;

            // replace the injected ad
            const adSource = _.find(adSources, { id });
            adCodeContainer.innerHTML = '';
            injectHtml(adCodeContainer, adSource.adCode);
            injectedAds.current[adCodeContainer] = id;
        }
    };


    const getProductAdRoots = () => {
        const rootElem = root.current;
        if (!rootElem) return null;

        const productAdRoots = Array.from(rootElem.querySelectorAll('[data-vp-native-ad]'));
        return productAdRoots.map(elem => [elem, Number(elem.dataset.vpNativeAd)]);
    };

    const setupProductAdTracking = () => {
        const productAdRoots = getProductAdRoots();

        // reset old ad view tracked flags
        const removedProductIds = getRemovedProductAds().map(pa => pa.id);
        for (const id of removedProductIds) {
            // clear view observer for product ad
            if (productAdViewObservers.current[id]) {
                productAdViewObservers.current[id].disconnect();
                productAdViewObservers.current[id] = null;
            }
            productAdViewTracked.current[id] = false;
        }

        const clickListeners = [];
        for (const [productAdRoot, productId] of productAdRoots) {
            if (isNewProductAdLoad(productId)) {
                trackProductAdEvent('load', productId);
                productAdViewObservers.current[productId] = new AdVisibilityObserver(productAdRoot, () => {
                    trackProductAdEvent('view', productId);
                });
            }

            const clickListener = () => {
                trackProductAdEvent('click', productId);
                trackCommerceUnitEvent('click');
                applyTrackingCodes && applyTrackingCodes('click');
            };
            productAdRoot.onclick = clickListener;
            clickListeners.push({ element: productAdRoot, listener: clickListener });
        }
        return clickListeners;
    };

    const getProgrammaticAdRoots = () => {
        const rootElem = root.current;
        if (!rootElem) return null;

        const programmaticAdRoots = Array.from(rootElem.querySelectorAll('[data-vp-programmatic-ad]'));
        return programmaticAdRoots.map(elem => [elem, Number(elem.dataset.vpProgrammaticAd)]);
    };

    const setupProgrammaticAdClickTracking = () => {
        clearInterval(iframeAdClickDetectionInterval.current);

        const rootElem = root.current;
        if (!rootElem) return;

        const programmaticAdRoots = getProgrammaticAdRoots();
        if (_.isEmpty(programmaticAdRoots)) return;

        // Detection of clicks on internal iframes that are put in the Ad code (it doesn't work through regular onclick capturing)
        iframeAdClickDetectionInterval.current = setInterval(() => {
            const iframeElem = document.activeElement;
            if (!iframeElem || iframeElem.tagName !== 'IFRAME') return;
            const [adRootElem, adSourceId] = programmaticAdRoots.find(([rootElem]) => isElementChildOf(iframeElem, rootElem));
            if (adRootElem) {
                iframeElem.blur();
                if (adSourceId) {
                    trackProgrammaticAdEvent('click', adSourceId);
                    trackCommerceUnitEvent('click');
                }
            }
        }, 200);

        const removedProgrammaticAdIds = getRemovedProgrammaticAds().map(pa => pa.id);
        for (const id of removedProgrammaticAdIds) {
            // clear view observer for programmatic ad
            if (programmaticAdViewObservers.current[id]) {
                programmaticAdViewObservers.current[id].disconnect();
                programmaticAdViewObservers.current[id] = null;
            }
            programmaticAdViewTracked.current[id] = false;
        }

        const clickListeners = [];
        for (const [adRoot, adSourceId] of programmaticAdRoots) {
            if (isNewProgrammaticAdLoad(adSourceId)) {
                if (isElementOrItsChildrenVisible(adRoot)) {
                    trackProgrammaticAdEvent('load', adSourceId);
                }
                productAdViewObservers.current[adSourceId] = new AdVisibilityObserver(adRoot, () => {
                    trackProgrammaticAdEvent('view', adSourceId);
                });
            }

            const clickListener = () => {
                trackProgrammaticAdEvent('click', adSourceId);
                trackCommerceUnitEvent('click');
                applyTrackingCodes && applyTrackingCodes('click');
            };
            adRoot.onlick = clickListener;
            clickListeners.push({ element: adRoot, listener: clickListener });
        }
        return clickListeners;
    };

    const trackCommerceUnitEvent = (action) => {
        const {
            publisherId,
            publisherName,
            campaignId,
            rendererId,
            adRenderer: {
                template
            }
        } = props;

        ClientAnalytics.sendCommerceUnitEvent(action, publisherId, template.id, campaignId, rendererId, publisherName);
    };

    const trackProductAdEvent = (action, productId) => {
        const {
            publisherId,
            publisherName,
            campaignId,
            rendererId,
            adRenderer: {
                template
            }
        } = props;
        const productData = _.find(products, { id: productId });
        const sourceId = _.get(productData, 'dataSource.id');
        const sourceName = _.get(productData, 'dataSource.title');
        ClientAnalytics.sendCommerceUnitProductAdEvent(action, publisherId, template.id, sourceId, productId, campaignId, rendererId, publisherName, sourceName);
    };

    const trackProgrammaticAdEvent = (action, adSourceId) => {
        const {
            publisherId,
            publisherName,
            campaignId,
            rendererId,
            adRenderer: {
                template
            }
        } = props;
        const adSourceData = _.find(adSources, { id: adSourceId });
        const sourceName = _.get(adSourceData, 'name');
        ClientAnalytics.sendCommerceUnitProgrammaticAdEvent(action, publisherId, template.id, adSourceId, campaignId, rendererId, publisherName, sourceName);
    };

    const initialize = async () => {
        await loadStyleScopedPolyfill();
        const adRendererDetails = await AdsApi.getAdRenderer(props.adRenderer.id);
        setAdRenderer(adRendererDetails);
    };

    /**
     * We're using scoped styles with injected templates (polyfill for <style scoped> from: https://github.com/samthor/scoped)
     * It helps avoiding css styling conflicts when different commerce units rendered on a page use same css rule paths
     * by scoping injected rules to the container element (through a dynamically injected class attribute)
     */
    const loadStyleScopedPolyfill = async () => {
        window.scopedCSS = {applyToClass: true, prefix: '_vup_scope'};
        require('style-scoped');
    };

    const getTemplateScript = () => {
        return _.get(props.adRenderer, 'template.templateScript');
    };

    const injectTemplateScript = () => {
        const templateScript = getTemplateScript();
        if (!templateScript) return;

        const compiled = compileHandlebarsTemplate(Handlebars, templateScript);
        const context = getTemplateContext();
        injectHtml(document.body, compiled(context));
    };

    const loadDisplayedAdsData = async () => {
        if (adRenderer) {
            const adSources = [];
            const { adRendererAdSources } = adRenderer;
            const { template: { adSourcesNumber } } = adRenderer;
            for (let i = 0; i < adSourcesNumber; i++) {
                const rendererAdSource = getRandomItem(adRendererAdSources);
                if (!rendererAdSource) continue;
                const { adTrackingId } = props;
                const adSource = interpolateAdSourceWithTrackingId(rendererAdSource.source, adTrackingId);
                adSources.push(adSource);
            }
            setAdSources(adSources);
        }
    };

    const loadDisplayedProductData = async () => {
        let adRendererProductSources = [];

        if (adRenderer) {
            adRendererProductSources = getRandomAdRendererProductSources();
            const uniquePublisherProductSources = _.uniqBy(adRendererProductSources, pps => pps.id);
            uniquePublisherProductSources.forEach((adRendererProductSource) => {
                const { source: sourceId } = adRendererProductSource;
                cachedProductDataSources.current[adRendererProductSource.id] = cachedProductDataSources.current[adRendererProductSource.id] || ProductsApi.getProductDataSource(sourceId);
            });

            const values = Object.values(cachedProductDataSources.current);
            const keys = Object.keys(cachedProductDataSources.current);
            const productsDataSources = await Promise.all(values);

            for (let i = 0; i < keys.length; i++) {
                cachedProductDataSources.current[keys[i]] = productsDataSources[i];
            }

            const products = [];
            adRendererProductSources.forEach((pps) => {
                products.push(chooseProductFromTopProducts(cachedProductDataSources.current[pps.id], pps.destinationUrl));
            });

            setProducts(products);
        }
    };

    const chooseProductFromTopProducts = (productsDS, publisherProductSourceUrl) => {
        const templateProduct = {
            ...getRandomItem(productsDS.topProducts, 50),
            publisherUrl: publisherProductSourceUrl,
            dataSource: {
                ..._.omit(productsDS, ['topProducts']),
                isPublisherType: productsDS.type === 'publisher'
            }
        };
        return templateProduct;
    };

    const getRandomItem = (array, maxIndex = -1) => {
        return array[Math.floor(Math.random() * (maxIndex >= 0 ? Math.min(maxIndex + 1, array.length) : array.length))];
    };

    const getRandomAdRendererProductSources = () => {
        const productSources = [];
        const { adRenderer: { adRendererProductSources } } = props;
        const { template: { productsSourcesNumber } } = adRenderer;
        for (let i = 0; i < productsSourcesNumber; i++) {
            const productSource = getRandomItem(adRendererProductSources.filter(ppds => ppds.isActive));
            if (productSource) productSources.push(productSource);
        }
        return productSources;
    };

    const getTemplateContext = () => {
        const { campaignId, displayDuration } = props;
        return {
            adSources,
            products,
            programmaticAdTimeToShow: programmaticAdTimeToShow || 0,
            campaignId,
            publisherCssReset,
            displayDuration,
         };
    };

    const toReactElement = () => {
        if (!compiledAdTemplate.current) return null;

        const context = getTemplateContext();
        const html = `<div class="vup-commerce-unit">${compiledAdTemplate.current(context)}</div>`;
        const htmlParser = Parser();
        return htmlParser.parse(html);
    };

    // render
    return (
      <div ref={root} className="vup-commerce-unit-root">
        {toReactElement()}
      </div>
    );
};

export default CommerceUnit;